import { useEffect, useState, type FC } from "react";

import { AutocompleteTypeEnum, useFetchEntitiesAutocomplete } from "@ds/hooks/use-fetch-entities";

import {
  AutoComplete,
  AutoCompleteGroupItem,
  AutoCompleteGroupLabel,
  type SingleAutoCompleteProps,
} from "@ds/components/forms/autocomplete";

import { getExperienceStatusIndicatorWithName } from "../../experiences/components/common/status-indicator";
import { isIncompleteExperience, type ExperiencesByCompletenessGroup } from "../../experiences/utils/model";

const renderItemTemplate = (experience: Experience) => {
  if (isIncompleteExperience(experience)) {
    return (
      <AutoCompleteGroupItem $isInvalid className="autocomplete-option--disabled">
        {getExperienceStatusIndicatorWithName(experience)}
      </AutoCompleteGroupItem>
    );
  }

  // Deployable experiences are listed without a section title, so they aren't indented under one
  return (
    <AutoCompleteGroupItem className="ml-0">{getExperienceStatusIndicatorWithName(experience)}</AutoCompleteGroupItem>
  );
};

// Only experiences that can't be deployed get a section title
const renderOptionGroupTemplate = ({ completeness_status }: ExperiencesByCompletenessGroup) =>
  completeness_status === "incomplete" ? <AutoCompleteGroupLabel>Incomplete experiences</AutoCompleteGroupLabel> : null;

export const DeploymentExperiencesAutoComplete: FC<Omit<SingleAutoCompleteProps<Experience>, "isLoading">> = ({
  customRef,
  inputRef,
  value,
  filterSuggestionsPredicate,
  dropdownMode,
  autoFocus,
  onBlur,
  onChange,
}) => {
  const [grouppedSuggestions, setGrouppedSuggestions] = useState<ExperiencesByCompletenessGroup[]>([]);

  const [
    suggestions,
    onCompleteHandler,
    {
      state: { status: remoteStatus },
    },
  ] = useFetchEntitiesAutocomplete<Experience>(AutocompleteTypeEnum.Experiences, {
    filterPredicate: filterSuggestionsPredicate,
  });

  useEffect(() => {
    const complete: ExperiencesByCompletenessGroup = { completeness_status: "complete", experiences: [] };
    const incomplete: ExperiencesByCompletenessGroup = { completeness_status: "incomplete", experiences: [] };
    suggestions.forEach(entry => {
      (isIncompleteExperience(entry) ? incomplete : complete).experiences.push(entry);
    });

    setGrouppedSuggestions([complete, incomplete].filter(({ experiences }) => experiences.length));
  }, [suggestions]);

  return (
    <AutoComplete
      field="name"
      placeholder="Enter experience name"
      itemTemplate={renderItemTemplate}
      optionGroupLabel="completeness_status"
      optionGroupChildren="experiences"
      optionGroupTemplate={renderOptionGroupTemplate}
      customRef={customRef}
      inputRef={inputRef}
      value={value}
      suggestions={grouppedSuggestions}
      dropdownMode={dropdownMode}
      autoFocus={autoFocus}
      isLoading={remoteStatus === "loading"}
      onBlur={onBlur}
      onChange={onChange}
      onComplete={onCompleteHandler}
    />
  );
};
